import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createBuylistFromParse,
  listBuylistSubmissions,
  BuylistSubmissionSummary,
} from '../api/buylist';
import { Button } from '../components/base/Button';
import { borderRadius, colors, spacing, typography } from '../styles/tokens';
import { BUYLIST_CONTACT_SOURCES } from '../utils/buylistOffer';
import { formatInstant } from '../utils/datetime';

const STATUS_TABS = [
  { id: '', label: 'Active' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'awaiting_shipment', label: 'Awaiting shipment' },
  { id: 'received', label: 'Received' },
  { id: 'grading', label: 'Grading' },
  { id: 'offer_ready', label: 'Offer ready' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'paid', label: 'Paid' },
  { id: 'expired', label: 'Expired' },
];

const formatMoney = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  return `$${value.toFixed(2)}`;
};

export const BuylistQueuePage: React.FC = () => {
  const [status, setStatus] = useState('');
  const [items, setItems] = useState<BuylistSubmissionSummary[]>([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [sellerEmail, setSellerEmail] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [contactSource, setContactSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listBuylistSubmissions(status || undefined);
      setItems(response.items);
      setBadgeCount(response.badgeCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load buylist queue');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const createStaffSubmission = async () => {
    setCreating(true);
    setCreateMessage(null);
    setError(null);
    try {
      const form = new FormData();
      if (file) form.append('file', file);
      if (pasteText.trim()) form.append('paste_text', pasteText.trim());
      if (sellerEmail.trim()) form.append('seller_email', sellerEmail.trim());
      if (sellerName.trim()) form.append('seller_name', sellerName.trim());
      if (contactSource.trim()) form.append('contact_source', contactSource.trim());
      const created = await createBuylistFromParse(form);
      setCreateMessage(`Created ${created.referenceCode || created.id} — parsing in background.`);
      setPasteText('');
      setFile(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create submission');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={{ ...typography.h1, margin: 0 }}>Buylist</h1>
          <p style={styles.lede}>
            Buy queue — customer Shopify submits and staff paste
            {badgeCount > 0 ? ` · ${badgeCount} active` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" type="button" onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      <section style={styles.card}>
        <h2 style={{ ...typography.h2, marginTop: 0, marginBottom: spacing.xs }}>Staff create</h2>
        <p style={styles.hint}>
          Paste or upload a list to create a submitted buylist without a Shopify customer. Optional
          seller email is used for label notification.
        </p>
        <div style={styles.formGrid}>
          <label style={styles.field}>
            <span style={styles.label}>Seller email (optional)</span>
            <input
              value={sellerEmail}
              onChange={(e) => setSellerEmail(e.target.value)}
              style={styles.input}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Seller name (optional)</span>
            <input
              value={sellerName}
              onChange={(e) => setSellerName(e.target.value)}
              style={styles.input}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Contact came from</span>
            <select
              value={contactSource}
              onChange={(e) => setContactSource(e.target.value)}
              style={styles.input}
            >
              <option value="">Unset</option>
              {BUYLIST_CONTACT_SOURCES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <span style={styles.label}>Upload list</span>
            <input
              type="file"
              accept=".txt,.csv,.tsv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <label style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <span style={styles.label}>Or paste list</span>
            <textarea
              rows={6}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              style={{ ...styles.input, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="One card per line or CSV columns"
            />
          </label>
          <div>
            <Button
              type="button"
              disabled={creating || (!file && !pasteText.trim())}
              loading={creating}
              onClick={() => void createStaffSubmission()}
            >
              Parse &amp; add to queue
            </Button>
          </div>
          {createMessage && <p style={styles.success}>{createMessage}</p>}
        </div>
      </section>

      <div style={styles.tabs}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id || 'active'}
            type="button"
            onClick={() => setStatus(tab.id)}
            style={{
              ...styles.tab,
              ...(status === tab.id ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <p style={styles.error}>{error}</p>}
      {loading && <p style={styles.hint}>Loading…</p>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Reference</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Market</th>
              <th style={styles.th}>Offer</th>
              <th style={styles.th}>Final</th>
              <th style={styles.th}>Paid</th>
              <th style={styles.th}>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td style={styles.td}>
                  <Link to={`/buylist/${item.id}`} style={styles.link}>
                    {item.referenceCode || item.id.slice(0, 8)}
                  </Link>
                </td>
                <td style={styles.td}>
                  <span style={styles.statusPill}>{item.status.replace(/_/g, ' ')}</span>
                </td>
                <td style={styles.td}>{formatMoney(item.marketTotal)}</td>
                <td style={styles.td}>
                  {formatMoney(item.quotedOfferMin)} – {formatMoney(item.quotedOfferMax)}
                </td>
                <td style={styles.td}>{formatMoney(item.finalOffer)}</td>
                <td style={styles.td}>{formatMoney(item.amountPaid)}</td>
                <td style={styles.td}>{formatInstant(item.submittedAt)}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...styles.td, color: colors.textSecondary }}>
                  No submissions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: spacing.lg,
    maxWidth: 1100,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  lede: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    marginBottom: 0,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    background: colors.surface,
    marginBottom: spacing.lg,
  },
  hint: {
    ...typography.small,
    color: colors.textSecondary,
    margin: `0 0 ${spacing.md}`,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.sm,
    maxWidth: 720,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  label: {
    ...typography.small,
    color: colors.textSecondary,
  },
  input: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    font: 'inherit',
    background: colors.backgroundAlt,
  },
  tabs: {
    display: 'flex',
    gap: spacing.xs,
    flexWrap: 'wrap',
    marginBottom: spacing.md,
  },
  tab: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: typography.small.fontSize,
  },
  tabActive: {
    borderColor: colors.primary,
    background: colors.primaryLight,
    color: colors.white,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: spacing.sm,
    textAlign: 'left',
    background: colors.gray100,
    ...typography.small,
    color: colors.textSecondary,
    fontWeight: 600,
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: spacing.sm,
    borderTop: `1px solid ${colors.border}`,
    ...typography.body,
  },
  link: {
    color: colors.primary,
    fontWeight: 600,
    textDecoration: 'none',
  },
  statusPill: {
    display: 'inline-block',
    padding: `2px ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    background: colors.gray100,
    ...typography.small,
    textTransform: 'capitalize' as const,
  },
  error: { color: colors.error },
  success: { color: colors.success, margin: 0, gridColumn: '1 / -1' },
};

export default BuylistQueuePage;
